import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, Webhook, Power, Save, Mail, MessageSquare, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

// Mirrors backend models.leads.RuleType — keep in sync.
const RULE_TYPES = [
  { value: "keyword",     label: "Keyword in message",  hint: "Match if message contains any of these words/phrases (case-insensitive)" },
  { value: "category",    label: "AI category",         hint: "Match when AI classifies the message as one of these categories" },
  { value: "new_number",  label: "New sender",          hint: "Match when the message is from a phone number we've never seen before" },
  { value: "chat_type",   label: "Chat type",           hint: "Match individual chats or group chats" },
  { value: "regex",       label: "Regex pattern",       hint: "Advanced: match a Python regex (case-insensitive)" },
];

const CATEGORY_OPTIONS = [
  "new_enquiry", "opportunity", "client_support", "follow_up",
  "deadline_commitment", "general_communication", "promotion",
];

const EMPTY = {
  enabled: false,
  webhook_enabled: false,
  webhook_url: "",
  webhook_headers: {},
  webhook_payload_format: "detailed",
  webhook_service: "",
  webhook_campaign: "whatsapp",
  webhook_require_mobile: true,
  slack_enabled: false,
  slack_webhook_url: "",
  email_enabled: false,
  email_recipients: [],
  rules: [],
};

export default function LeadDetectionSettings() {
  const [s, setS] = useState(EMPTY);
  const [headersRaw, setHeadersRaw] = useState("");
  const [emailsRaw, setEmailsRaw] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads/settings");
      const next = {
        ...EMPTY,
        ...r.data,
        webhook_url: r.data.webhook_url || "",
        webhook_service: r.data.webhook_service || "",
        webhook_campaign: r.data.webhook_campaign || "whatsapp",
        webhook_payload_format: r.data.webhook_payload_format || "detailed",
        webhook_require_mobile: r.data.webhook_require_mobile !== false,
        slack_webhook_url: r.data.slack_webhook_url || "",
        webhook_headers: r.data.webhook_headers || {},
        email_recipients: r.data.email_recipients || [],
        rules: r.data.rules || [],
      };
      setS(next);
      setHeadersRaw(
        Object.entries(next.webhook_headers).map(([k, v]) => `${k}: ${v}`).join("\n")
      );
      setEmailsRaw((next.email_recipients || []).join(", "));
    } catch {
      toast.error("Could not load lead settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const parseHeaders = () => {
    const headers = {};
    headersRaw.split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx <= 0) return;
      const k = line.slice(0, idx).trim();
      const v = line.slice(idx + 1).trim();
      if (k) headers[k] = v;
    });
    return headers;
  };

  const parseEmails = () =>
    emailsRaw.split(/[,\n;]/).map((e) => e.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...s,
        webhook_headers: parseHeaders(),
        email_recipients: parseEmails(),
      };
      const r = await api.put("/leads/settings", payload);
      const next = {
        ...EMPTY,
        ...r.data,
        webhook_url: r.data.webhook_url || "",
        webhook_service: r.data.webhook_service || "",
        webhook_campaign: r.data.webhook_campaign || "whatsapp",
        webhook_payload_format: r.data.webhook_payload_format || "detailed",
        webhook_require_mobile: r.data.webhook_require_mobile !== false,
        slack_webhook_url: r.data.slack_webhook_url || "",
        webhook_headers: r.data.webhook_headers || {},
        email_recipients: r.data.email_recipients || [],
        rules: r.data.rules || [],
      };
      setS(next);
      setEmailsRaw((next.email_recipients || []).join(", "));
      toast.success("Lead detection settings saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        ...s,
        webhook_headers: parseHeaders(),
        email_recipients: parseEmails(),
      };
      const r = await api.post("/leads/settings/test", payload);
      setTestResult(r.data.channels || {});
    } catch (e) {
      toast.error("Test failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  const addRule = () => {
    setS({
      ...s,
      rules: [...s.rules, { rule_type: "keyword", value: "", enabled: true }],
    });
  };
  const updateRule = (idx, patch) => {
    const next = [...s.rules];
    next[idx] = { ...next[idx], ...patch };
    setS({ ...s, rules: next });
  };
  const removeRule = (idx) => {
    setS({ ...s, rules: s.rules.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Webhook size={18} className="text-violet-600" />
            <h2 className="text-[17px] font-bold text-slate-900">Lead Detection & Notifications</h2>
          </div>
          <p className="text-[13px] text-slate-500">
            Identify incoming WhatsApp messages as leads using your rules, then push them to your CRM, Slack channel, or email — all three can be enabled independently.
          </p>
        </div>
        <button
          onClick={() => setS({ ...s, enabled: !s.enabled })}
          className={"shrink-0 h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-[12.5px] font-semibold border transition " + (s.enabled
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : "bg-slate-50 border-slate-200 text-slate-500")}
        >
          <Power size={13} /> {s.enabled ? "Enabled" : "Disabled"}
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-6 text-center">Loading…</div>
      ) : (
        <div className={"space-y-5 " + (s.enabled ? "" : "opacity-60 pointer-events-none")}>

          {/* Channel 1: Webhook */}
          <ChannelCard
            title="CRM Webhook"
            icon={<Webhook size={14} className="text-blue-600" />}
            enabled={s.webhook_enabled}
            onToggle={() => setS({ ...s, webhook_enabled: !s.webhook_enabled })}
            description="POST a JSON payload to any URL — your CRM, Zapier, n8n, Make, etc."
          >
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">Webhook URL</label>
            <input
              type="url"
              placeholder="https://your-crm.example.com/api/leads"
              value={s.webhook_url}
              onChange={(e) => setS({ ...s, webhook_url: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition font-mono"
            />

            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3 block">Payload Format</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setS({ ...s, webhook_payload_format: "detailed" })}
                className={"text-left p-3 rounded-lg border transition " + (s.webhook_payload_format === "detailed"
                  ? "border-blue-600 bg-blue-50"
                  : "border-slate-200 hover:border-slate-400")}
              >
                <div className="text-[12.5px] font-bold text-slate-900">Detailed</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Full nested Brainydo schema. Only for CRMs built to accept it.</div>
              </button>
              <button
                onClick={() => setS({ ...s, webhook_payload_format: "simple" })}
                className={"text-left p-3 rounded-lg border transition " + (s.webhook_payload_format === "simple"
                  ? "border-blue-600 bg-blue-50"
                  : "border-slate-200 hover:border-slate-400")}
              >
                <div className="text-[12.5px] font-bold text-slate-900">Simple flat</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{`{ name, mobile, service, message, campaign }`} — recommended for most CRMs.</div>
              </button>
            </div>

            {s.webhook_payload_format === "simple" && (
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 block">Service</label>
                  <input
                    type="text"
                    placeholder="e.g. Skin Treatment   (or use {category} / {title})"
                    value={s.webhook_service}
                    onChange={(e) => setS({ ...s, webhook_service: e.target.value })}
                    className="w-full h-9 px-3 border border-slate-200 rounded text-[13px] focus:border-blue-600 outline-none bg-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 block">Campaign</label>
                  <input
                    type="text"
                    placeholder="whatsapp"
                    value={s.webhook_campaign}
                    onChange={(e) => setS({ ...s, webhook_campaign: e.target.value })}
                    className="w-full h-9 px-3 border border-slate-200 rounded text-[13px] focus:border-blue-600 outline-none bg-white"
                  />
                </div>
                <div className="text-[11px] text-slate-500">
                  Placeholders supported: <code className="text-slate-700">{"{category}"}</code>, <code className="text-slate-700">{"{title}"}</code>, <code className="text-slate-700">{"{summary}"}</code>, <code className="text-slate-700">{"{client_name}"}</code>, <code className="text-slate-700">{"{priority}"}</code>, <code className="text-slate-700">{"{matched_rules}"}</code>
                </div>

                {/* When the CRM lookup happens after lead capture: what to do
                    if WhatsApp didn't give us a phone for that sender */}
                <div className="border-t border-slate-200 pt-3">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">When mobile is missing</div>
                  <div className="space-y-1.5">
                    <label className={"flex items-start gap-2.5 p-2.5 border rounded-lg cursor-pointer transition bg-white " +
                      (s.webhook_require_mobile ? "border-blue-500" : "border-slate-200 hover:border-slate-400")}>
                      <input
                        type="radio"
                        name="require_mobile"
                        checked={!!s.webhook_require_mobile}
                        onChange={() => setS({ ...s, webhook_require_mobile: true })}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-[12.5px] font-semibold text-slate-900">Hold the lead — let me add the mobile</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Brainydo captures the lead but doesn't POST it yet. You'll see it on
                          <strong> /leads</strong> with status <strong>Skipped: no mobile</strong> — type the number
                          there and click Resend. Safer for CRMs that reject empty-mobile payloads with HTTP 400.
                        </div>
                      </div>
                    </label>
                    <label className={"flex items-start gap-2.5 p-2.5 border rounded-lg cursor-pointer transition bg-white " +
                      (!s.webhook_require_mobile ? "border-blue-500" : "border-slate-200 hover:border-slate-400")}>
                      <input
                        type="radio"
                        name="require_mobile"
                        checked={!s.webhook_require_mobile}
                        onChange={() => setS({ ...s, webhook_require_mobile: false })}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-[12.5px] font-semibold text-slate-900">Send it anyway with blank mobile</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          POST every lead to the CRM regardless. <code className="text-slate-700">mobile</code> may be empty —
                          your CRM may reject some. Choose this when you'd rather see a 400 in delivery
                          history than miss a lead.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">Preview</div>
                  <pre className="p-2.5 bg-slate-900 text-slate-100 text-[11px] rounded overflow-x-auto font-mono leading-relaxed">{JSON.stringify({
                    name: "Rajesh",
                    mobile: "9876543210",
                    service: (s.webhook_service || "").replace("{category}", "new_enquiry").replace("{title}", "Interested in consultation"),
                    message: "Interested in consultation",
                    campaign: (s.webhook_campaign || "whatsapp").replace("{category}", "new_enquiry").replace("{title}", "Interested in consultation"),
                  }, null, 2)}</pre>
                </div>
              </div>
            )}

            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-3 block">Custom Headers (optional)</label>
            <textarea
              rows={3}
              placeholder={"Authorization: Bearer YOUR_TOKEN\nX-Source: Brainydo"}
              value={headersRaw}
              onChange={(e) => setHeadersRaw(e.target.value)}
              className="w-full p-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 outline-none transition font-mono resize-y"
            />
            <p className="text-[11px] text-slate-400 mt-1">One header per line, format <code className="text-slate-600">Header-Name: value</code>.</p>
          </ChannelCard>

          {/* Channel 2: Slack */}
          <ChannelCard
            title="Slack"
            icon={<MessageSquare size={14} className="text-violet-600" />}
            enabled={s.slack_enabled}
            onToggle={() => setS({ ...s, slack_enabled: !s.slack_enabled })}
            description="Post each lead to a Slack channel using an incoming-webhook URL."
          >
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">Slack Incoming Webhook URL</label>
            <input
              type="url"
              placeholder="https://hooks.slack.com/services/T.../B.../..."
              value={s.slack_webhook_url}
              onChange={(e) => setS({ ...s, slack_webhook_url: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 outline-none transition font-mono"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Create one at <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="text-blue-600 underline">api.slack.com/messaging/webhooks</a> → bind to the channel where leads should appear.
            </p>
          </ChannelCard>

          {/* Channel 3: Email */}
          <ChannelCard
            title="Email"
            icon={<Mail size={14} className="text-emerald-600" />}
            enabled={s.email_enabled}
            onToggle={() => setS({ ...s, email_enabled: !s.email_enabled })}
            description="Send a formatted email to one or more recipients on each new lead."
          >
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 block">Recipient Email(s)</label>
            <input
              type="text"
              placeholder="sales@yourcompany.com, manager@yourcompany.com"
              value={emailsRaw}
              onChange={(e) => setEmailsRaw(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 outline-none transition"
            />
            <p className="text-[11px] text-slate-400 mt-1">Comma-separated. Each address receives every captured lead.</p>
          </ChannelCard>

          {/* Rules */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Lead Rules ({s.rules.length})</label>
                <p className="text-[11.5px] text-slate-400 mt-0.5">Any enabled rule that matches qualifies the message as a lead.</p>
              </div>
              <button
                onClick={addRule}
                className="h-8 px-3 inline-flex items-center gap-1 text-[12.5px] border border-slate-200 hover:border-slate-900 rounded-lg transition"
              >
                <Plus size={12} /> Add rule
              </button>
            </div>
            {s.rules.length === 0 ? (
              <div className="p-5 border border-dashed border-slate-200 rounded-lg text-center text-[13px] text-slate-400">
                No rules yet — without a rule, no messages will be captured as leads.
              </div>
            ) : (
              <div className="space-y-2">
                {s.rules.map((rule, i) => (
                  <RuleRow key={i} rule={rule} onChange={(patch) => updateRule(i, patch)} onRemove={() => removeRule(i)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test results — show per-channel outcome from the last "Send test" click */}
      {testResult && (
        <div className="mt-4 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Test Results</div>
          {Object.entries(testResult).map(([channel, result]) => {
            if (result.status === "skipped") {
              return (
                <div key={channel} className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-500">
                  <span className="font-bold capitalize">{channel}:</span> Skipped ({result.reason})
                </div>
              );
            }
            const ok = result.status === "success";
            return (
              <div key={channel} className={"p-3 border rounded-lg text-[12px] " + (ok
                ? "bg-emerald-50 border-emerald-200"
                : "bg-red-50 border-red-200")}>
                <div className="flex items-center gap-2 font-bold">
                  {ok ? <CheckCircle2 size={13} className="text-emerald-600" /> : <AlertCircle size={13} className="text-red-600" />}
                  <span className="capitalize">{channel}</span>
                  {result.status_code && <span className="font-mono text-[11px] text-slate-500">HTTP {result.status_code}</span>}
                </div>
                {result.error && (
                  <div className={"mt-1 text-[11px] break-all " + (ok ? "text-slate-600" : "text-red-700")}>
                    {result.error}
                  </div>
                )}
                {result.sent_payload && (
                  <details className="mt-2">
                    <summary className="text-[10.5px] text-slate-500 cursor-pointer hover:text-slate-700">Sent body</summary>
                    <pre className="mt-1 p-2 bg-slate-900 text-slate-100 text-[10.5px] rounded overflow-x-auto font-mono leading-relaxed">{JSON.stringify(result.sent_payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
        <button
          onClick={runTest}
          disabled={testing}
          className="h-10 px-4 inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-900 rounded-xl text-[13px] font-medium transition disabled:opacity-60"
          title="Send a sample lead to each enabled channel — bypasses DB, useful for debugging"
        >
          <Send size={13} /> {testing ? "Sending…" : "Send test"}
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="h-10 px-5 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition disabled:opacity-60 active:scale-95"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function ChannelCard({ title, icon, enabled, onToggle, description, children }) {
  return (
    <div className={"border rounded-lg transition " + (enabled ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50")}>
      <div className="flex items-center gap-3 p-3 border-b border-slate-100">
        <div className="w-8 h-8 grid place-items-center bg-slate-50 border border-slate-200 rounded">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-slate-900">{title}</div>
          <div className="text-[11.5px] text-slate-500 truncate">{description}</div>
        </div>
        <button
          onClick={onToggle}
          className={"shrink-0 h-7 px-2.5 inline-flex items-center gap-1 rounded text-[11px] font-semibold border transition " + (enabled
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : "bg-white border-slate-200 text-slate-500")}
        >
          <Power size={10} /> {enabled ? "On" : "Off"}
        </button>
      </div>
      <div className={"p-4 " + (enabled ? "" : "opacity-60 pointer-events-none")}>{children}</div>
    </div>
  );
}

function RuleRow({ rule, onChange, onRemove }) {
  const meta = RULE_TYPES.find((t) => t.value === rule.rule_type);
  return (
    <div className="p-3 border border-slate-200 rounded-lg bg-slate-50">
      <div className="flex items-center gap-2 mb-2">
        <input
          type="checkbox"
          checked={rule.enabled !== false}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          className="accent-blue-600"
        />
        <select
          value={rule.rule_type}
          onChange={(e) => onChange({ rule_type: e.target.value, value: defaultValueFor(e.target.value) })}
          className="h-9 px-2 border border-slate-200 rounded-lg bg-white text-[13px] focus:border-blue-600 outline-none"
        >
          {RULE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <button
          onClick={onRemove}
          className="ml-auto h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 transition"
        >
          <Trash2 size={13} />
        </button>
      </div>
      <div className="ml-6">
        <RuleValueEditor rule={rule} onChange={onChange} />
        {meta?.hint && <p className="text-[11px] text-slate-500 mt-1.5">{meta.hint}</p>}
      </div>
    </div>
  );
}

function KeywordValueEditor({ rule, onChange }) {
  const list = Array.isArray(rule.value) ? rule.value : (rule.value ? [rule.value] : []);
  // Keep the raw text while typing so spaces and trailing commas survive; the
  // parent only ever sees the parsed list.
  const [draft, setDraft] = useState(list.join(", "));

  useEffect(() => {
    const parsed = draft.split(",").map((s) => s.trim()).filter(Boolean);
    if (parsed.join(" ") !== list.join(" ")) setDraft(list.join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.value]);

  return (
    <input
      type="text"
      placeholder="interested, quote, pricing, need help"
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange({ value: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) });
      }}
      onBlur={() => setDraft(draft.split(",").map((s) => s.trim()).filter(Boolean).join(", "))}
      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 outline-none bg-white"
    />
  );
}

function RuleValueEditor({ rule, onChange }) {
  switch (rule.rule_type) {
    case "keyword":
      return <KeywordValueEditor rule={rule} onChange={onChange} />;
    case "category": {
      const selected = new Set(Array.isArray(rule.value) ? rule.value : []);
      return (
        <div className="flex flex-wrap gap-1.5">
          {CATEGORY_OPTIONS.map((c) => {
            const on = selected.has(c);
            return (
              <button
                key={c}
                onClick={() => {
                  const next = new Set(selected);
                  if (on) next.delete(c); else next.add(c);
                  onChange({ value: [...next] });
                }}
                className={"text-[11.5px] px-2 py-1 rounded border transition " + (on
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400")}
              >
                {c}
              </button>
            );
          })}
        </div>
      );
    }
    case "new_number":
      return <div className="text-[12px] text-slate-500">No configuration — fires for any new sender.</div>;
    case "chat_type":
      return (
        <select
          value={rule.value || "individual"}
          onChange={(e) => onChange({ value: e.target.value })}
          className="h-9 px-2 border border-slate-200 rounded-lg bg-white text-[13px] focus:border-blue-600 outline-none"
        >
          <option value="individual">Individual</option>
          <option value="group">Group</option>
        </select>
      );
    case "regex":
      return (
        <input
          type="text"
          placeholder="^(hi|hello|hey).*price"
          value={rule.value || ""}
          onChange={(e) => onChange({ value: e.target.value })}
          className="w-full h-9 px-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 outline-none bg-white font-mono"
        />
      );
    default:
      return null;
  }
}

function defaultValueFor(type) {
  switch (type) {
    case "keyword":    return [];
    case "category":   return ["new_enquiry"];
    case "new_number": return null;
    case "chat_type":  return "individual";
    case "regex":      return "";
    default:           return null;
  }
}
